import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS as SEC_ENDPOINTS } from '../../Seguridad/Utils/Endpoints';
import { ENDPOINTS as LMS_ENDPOINTS } from '../Utils/Endpoints';
import { getSessionUserId } from '../../../utils/SessionUser';
import '../Styles/SolicitarCertificadoModal.css';

import AlertDialog from './Shared/AlertDialog';

export default function SolicitarCertificadoModal({ open, onClose, courseId, courseName }) {
    const [loading, setLoading] = useState(false);
    const [administradores, setAdministradores] = useState([]);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [sending, setSending] = useState(false);
    const [existingRequest, setExistingRequest] = useState(null);
    const [showForm, setShowForm] = useState(true);

    // Estado para el alert personalizado
    const [alertState, setAlertState] = useState({
        open: false,
        title: '',
        message: '',
        variant: 'info',
        callback: null
    });

    const handleCloseAlert = () => {
        const cb = alertState.callback;
        setAlertState(prev => ({ ...prev, open: false }));
        if (cb) cb();
    };

    useEffect(() => {
        if (open) {
            setShowForm(true);
            loadAdministradoresLMS();
        }
    }, [open]);

    const loadAdministradoresLMS = async () => {
        setLoading(true);
        try {
            const idUsuarioActual = getSessionUserId();

            // 1. Cargar datos necesarios y peticiones existentes
            const [resRoles, resUsuarioRol, resUsuarios, resEmpleados, resUnidades, resPersonas, resPeticiones] = await Promise.all([
                axios.get(SEC_ENDPOINTS.roles),
                axios.get(SEC_ENDPOINTS.usuarioRol),
                axios.get(SEC_ENDPOINTS.usuarios),
                axios.get(SEC_ENDPOINTS.empleados),
                axios.get(SEC_ENDPOINTS.unidadesOrgEmpleado),
                axios.get(SEC_ENDPOINTS.personas),
                axios.get(LMS_ENDPOINTS.peticionesCertificado)
            ]);

            const roles = resRoles.data || [];
            const usuarioRoles = resUsuarioRol.data || [];
            const usuarios = resUsuarios.data || [];
            const empleados = resEmpleados.data || [];
            const unidades = resUnidades.data || [];
            const personas = resPersonas.data || [];
            const peticiones = resPeticiones.data || [];

            // 2. Revisar si ya existe una petición PENDIENTE para este usuario y curso
            const idCursoInt = parseInt(courseId, 10);
            const misPeticiones = peticiones.filter(p =>
                (p.idCurso === idCursoInt || p.IdCurso === idCursoInt) &&
                (p.idUsuarioSolicitante === parseInt(idUsuarioActual, 10) || p.IdUsuarioSolicitante === parseInt(idUsuarioActual, 10)) &&
                p.activo !== false // Asumiendo que false significa eliminado lógico
            );

            // Buscamos si hay alguna NO atendida (Pendiente)
            const peticionPendiente = misPeticiones.find(p =>
                (p.atendido === false || p.Atendido === false || p.atendido === null || p.Atendido === null)
            );

            // 3. Encontrar el rol "Administrador LMS"
            const rolAdminLMS = roles.find(r =>
                (r.nombreRol || r.NombreRol) === 'Administrador LMS' ||
                (r.idRol || r.IdRol) === 1008
            );

            const idRolAdminLMS = rolAdminLMS ? (rolAdminLMS.idRol || rolAdminLMS.IdRol) : 1008;

            // 4. Obtener usuarios con rol "Administrador LMS" directamente del endpoint especializado
            const resAdmins = await axios.get(SEC_ENDPOINTS.usuariosByRol(idRolAdminLMS));
            const usuariosConRolAdmin = resAdmins.data || [];

            // 5. Obtener mis unidades organizacionales
            // En lugar de buscar en el array masivo, intentamos obtener el empleado del usuario actual
            const miUsuario = usuarios.find(u => String(u.idUsuario || u.IdUsuario) === String(idUsuarioActual));
            const misUnidadesIds = new Set();

            if (miUsuario) {
                const idPersonaMiUsuario = miUsuario.idPersona || miUsuario.IdPersona;
                const miEmpleado = empleados.find(e => (e.idPersona || e.IdPersona) === idPersonaMiUsuario);
                
                if (miEmpleado) {
                    const idEmpleadoMiUsuario = miEmpleado.idEmpleado || miEmpleado.IdEmpleado;
                    unidades
                        .filter(u => (u.idEmpleado || u.IdEmpleado) === idEmpleadoMiUsuario)
                        .forEach(u => misUnidadesIds.add(u.idUnidadOrganizacional || u.IdUnidadOrganizacional));
                }
            }

            // 6. Procesar todos los administradores encontrados
            const todosLosAdmins = usuariosConRolAdmin
                .map(u => {
                    const idPersona = u.idPersona || u.IdPersona;
                    const idUsuario = u.idUsuario || u.IdUsuario;
                    const empleado = empleados.find(e => (e.idPersona || e.IdPersona) === idPersona);
                    const persona = personas.find(p => (p.idPersona || p.IdPersona) === idPersona);

                    let comparteUnidad = false;
                    if (empleado && misUnidadesIds.size > 0) {
                        const idEmpleadoAdmin = empleado.idEmpleado || empleado.IdEmpleado;
                        const unidadesAdmin = unidades
                            .filter(un => (un.idEmpleado || un.IdEmpleado) === idEmpleadoAdmin)
                            .map(un => un.idUnidadOrganizacional || un.IdUnidadOrganizacional);

                        comparteUnidad = unidadesAdmin.some(uId => misUnidadesIds.has(uId));
                    }

                    const nombreCompleto = persona
                        ? `${persona.nombre || persona.Nombre || ''} ${persona.apellidoPaterno || persona.ApellidoPaterno || ''} ${persona.apellidoMaterno || persona.ApellidoMaterno || ''}`.trim()
                        : (u.nombreUsuario || u.NombreUsuario);

                    return {
                        idUsuario: idUsuario,
                        idPersona: idPersona,
                        nombreUsuario: u.nombreUsuario || u.NombreUsuario,
                        nombreCompleto: nombreCompleto || 'Sin nombre',
                        comparteUnidad: comparteUnidad
                    };
                });

            // 7. Filtrar: Priorizar los que comparten unidad, pero mostrar todos si es necesario
            let adminsFiltrados = todosLosAdmins.filter(a => a.comparteUnidad);

            if (adminsFiltrados.length === 0) {
                adminsFiltrados = todosLosAdmins;
            }

            setAdministradores(adminsFiltrados);

            setAdministradores(adminsFiltrados);

            if (peticionPendiente) {
                const adminSolicitadoId = peticionPendiente.idUsuarioSolicitado || peticionPendiente.IdUsuarioSolicitado;
                const adminSolicitado = adminsFiltrados.find(a => a.idUsuario === adminSolicitadoId);

                setExistingRequest({
                    ...peticionPendiente,
                    adminName: adminSolicitado ? adminSolicitado.nombreCompleto : 'Administrador no encontrado',
                    fecha: peticionPendiente.fechaPeticion || peticionPendiente.FechaPeticion
                });

                if (adminSolicitado) {
                    setSelectedAdmin(adminSolicitado);
                }
            } else {
                setExistingRequest(null);
            }

        } catch (error) {
            console.error('Error al cargar administradores LMS:', error);
            setAlertState({
                open: true,
                title: 'Error',
                message: 'Error al cargar la lista de administradores.',
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSolicitar = async () => {
        if (!selectedAdmin) {
            setAlertState({
                open: true,
                title: 'Atención',
                message: 'Por favor selecciona un administrador.',
                variant: 'warning'
            });
            return;
        }

        console.log('📧 [SolicitarCertificado] ========== INICIO DE SOLICITUD ==========');
        console.log(`📧 [SolicitarCertificado] Curso: ${courseName} (ID: ${courseId})`);
        console.log(`📧 [SolicitarCertificado] Administrador seleccionado:`, selectedAdmin);

        setSending(true);
        try {
            // 0. Guardar la petición en BD
            const idUsuarioSolicitante = getSessionUserId();
            const peticionDTO = {
                IdPeticion: 0,
                IdCurso: parseInt(courseId, 10),
                IdUsuarioSolicitado: selectedAdmin.idUsuario,
                IdUsuarioSolicitante: parseInt(idUsuarioSolicitante, 10), // Correct field name
                FechaPeticion: new Date().toISOString(),
                Atendido: false,
                IdUsuarioAtendio: null,
                Activo: true
            };

            await axios.post(LMS_ENDPOINTS.peticionesCertificado, peticionDTO);
            console.log('✅ [SolicitarCertificado] Petición guardada en BD');

            // --- Bloque Opcional: Envío de Correo ---
            try {
                // 1. Obtener el usuario seleccionado
                console.log(`📧 [SolicitarCertificado] Paso 1: Obteniendo datos del usuario ${selectedAdmin.idUsuario}...`);
                const resUsuario = await axios.get(SEC_ENDPOINTS.usuarioById(selectedAdmin.idUsuario));
                const usuarioRaw = resUsuario.data;
                const usuario = {
                    idUsuario: usuarioRaw?.idUsuario || usuarioRaw?.IdUsuario,
                    idPersona: usuarioRaw?.idPersona || usuarioRaw?.IdPersona,
                    nombreUsuario: usuarioRaw?.nombreUsuario || usuarioRaw?.NombreUsuario
                };

                if (!usuario || !usuario.idPersona) {
                    if (selectedAdmin.idPersona) {
                        usuario.idPersona = selectedAdmin.idPersona;
                        usuario.idUsuario = selectedAdmin.idUsuario;
                        usuario.nombreUsuario = selectedAdmin.nombreUsuario;
                    } else {
                        console.warn('⚠️ No se pudo obtener la información del usuario para el correo.');
                        throw new Error('SKIP_EMAIL');
                    }
                }

                // 2. Obtener TODOS los correos
                console.log(`📧 [SolicitarCertificado] Paso 2: Obteniendo todos los correos...`);
                const resCorreos = await axios.get(`${SEC_ENDPOINTS.personas.replace('/Personas', '')}/Emails`);
                const todosCorreos = resCorreos.data || [];

                // 3. Filtrar correos de esta persona
                const correosPersona = todosCorreos.filter(c =>
                    (c.idPersona || c.IdPersona) === usuario.idPersona
                );

                if (correosPersona.length === 0) {
                    console.log('⚠️ El administrador seleccionado no tiene correos registrados. Saltando envío.');
                    throw new Error('SKIP_EMAIL');
                }

                // 4. Buscar correo principal o tomar el primero
                let correoPrincipal = correosPersona.find(c =>
                    c.principal === true || c.Principal === true
                );

                if (!correoPrincipal) {
                    correoPrincipal = correosPersona[0];
                }

                const emailDestino = correoPrincipal.correo || correoPrincipal.Correo;

                if (!emailDestino) {
                    console.log('⚠️ No se pudo obtener el correo válido del administrador. Saltando envío.');
                    throw new Error('SKIP_EMAIL');
                }

                // 5. Obtener información del usuario solicitante
                const resSolicitante = await axios.get(SEC_ENDPOINTS.usuarioById(idUsuarioSolicitante));
                const solicitanteRaw = resSolicitante.data;
                const solicitante = {
                    idUsuario: solicitanteRaw?.idUsuario || solicitanteRaw?.IdUsuario,
                    idPersona: solicitanteRaw?.idPersona || solicitanteRaw?.IdPersona,
                    nombreUsuario: solicitanteRaw?.nombreUsuario || solicitanteRaw?.NombreUsuario
                };

                if (solicitante && solicitante.idPersona) {
                    const resPersonaSolicitante = await axios.get(SEC_ENDPOINTS.personasById(solicitante.idPersona));
                    const personaSolicitanteRaw = resPersonaSolicitante.data;
                    const personaSolicitante = {
                        nombre: personaSolicitanteRaw?.nombre || personaSolicitanteRaw?.Nombre || '',
                        apellidoPaterno: personaSolicitanteRaw?.apellidoPaterno || personaSolicitanteRaw?.ApellidoPaterno || '',
                        apellidoMaterno: personaSolicitanteRaw?.apellidoMaterno || personaSolicitanteRaw?.ApellidoMaterno || ''
                    };

                    const nombreSolicitante = `${personaSolicitante.nombre} ${personaSolicitante.apellidoPaterno} ${personaSolicitante.apellidoMaterno}`.trim();

                    // 6. Enviar correo
                    const correoData = {
                        para: emailDestino,
                        asunto: `Solicitud de Certificado - ${courseName}`,
                        cuerpoTexto: `Hola ${selectedAdmin.nombreCompleto},
    
${nombreSolicitante} (${solicitante.nombreUsuario}) ha solicitado la generación del certificado para el curso:

📚 Curso: ${courseName}

Por favor, revisa y genera el certificado correspondiente desde el panel administrativo.

Saludos,
Sistema LMS - SigmaKey`
                    };

                    await axios.post(`${SEC_ENDPOINTS.personas.replace('/Personas', '')}/Correo`, correoData);
                    console.log(`✅ [SolicitarCertificado] Correo enviado a: ${emailDestino}`);
                }
            } catch (emailError) {
                if (emailError.message !== 'SKIP_EMAIL') {
                    console.warn('⚠️ Error no crítico en envío de correo:', emailError.message);
                }
            }

            setShowForm(false);
            setAlertState({
                open: true,
                title: 'Éxito',
                message: 'Solicitud enviada exitosamente',
                variant: 'success',
                callback: onClose
            });
        } catch (error) {
            console.error('❌ [SolicitarCertificado] Error al enviar solicitud:', error);
            console.error('❌ [SolicitarCertificado] Detalles del error:', error.response?.data || error.message);
            setAlertState({
                open: true,
                title: 'Error',
                message: error.message || 'Error al enviar la solicitud de certificado.',
                variant: 'error'
            });
        } finally {
            setSending(false);
        }
    };

    if (!open) return null;

    return (
        <>
            {showForm && (
                <div className="solicitar-cert-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
                    <div className="solicitar-cert-modal">
                        <div className="solicitar-cert-header">
                            <h2>📜 Solicitar Certificado</h2>
                            <button className="solicitar-cert-close" onClick={onClose}>✕</button>
                        </div>

                        <div className="solicitar-cert-body">
                            {existingRequest ? (
                                <div className="solicitar-cert-existing-msg">
                                    <p>
                                        ✅ Ya has solicitado la verificación de este certificado a <strong>{existingRequest.adminName}</strong> el día {new Date(existingRequest.fecha).toLocaleDateString()}.
                                    </p>
                                    <p className="solicitar-cert-hint">
                                        No es necesario enviar otra solicitud. Si tienes dudas, contacta directamente al administrador.
                                    </p>
                                </div>
                            ) : (
                                <p className="solicitar-cert-description">
                                    Selecciona un administrador LMS que comparta tu unidad organizacional para solicitar la generación del certificado del curso <strong>{courseName}</strong>.
                                </p>
                            )}

                            {loading ? (
                                <div className="solicitar-cert-loading">
                                    <p>Cargando administradores...</p>
                                </div>
                            ) : administradores.length === 0 ? (
                                <div className="solicitar-cert-empty">
                                    <p>No hay administradores LMS disponibles que compartan tu unidad organizacional.</p>
                                </div>
                            ) : (
                                <div className="solicitar-cert-list">
                                    {administradores.map((admin) => (
                                        <div
                                            key={admin.idUsuario}
                                            className={`solicitar-cert-item ${selectedAdmin?.idUsuario === admin.idUsuario ? 'selected' : ''} ${existingRequest ? 'disabled' : ''}`}
                                            onClick={() => !existingRequest && setSelectedAdmin(admin)}
                                        >
                                            <div className="solicitar-cert-item-avatar">
                                                {admin.nombreCompleto.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="solicitar-cert-item-info">
                                                <h4>{admin.nombreCompleto}</h4>
                                                <p>@{admin.nombreUsuario}</p>
                                            </div>
                                            {selectedAdmin?.idUsuario === admin.idUsuario && (
                                                <div className="solicitar-cert-item-check">✓</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="solicitar-cert-footer">
                            <button className="btn-cancel" onClick={onClose}>
                                Cerrar
                            </button>
                            {!existingRequest && (
                                <button
                                    className="btn-action"
                                    onClick={handleSolicitar}
                                    disabled={!selectedAdmin || sending || loading}
                                >
                                    {sending ? 'Enviando...' : 'Enviar Solicitud'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <AlertDialog
                open={alertState.open}
                onClose={handleCloseAlert}
                title={alertState.title}
                message={alertState.message}
                variant={alertState.variant}
            />
        </>
    );
}
